import React, {useEffect, useState} from 'react';
import {chatAPI} from "../services/api";

interface ImageCompProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    path: string;
    type?: string;
    classNameContainer?: string;
}

export const imageCache = new Map<string, string>();

const loadingPromises = new Map<string, Promise<string>>();

async function fetchAndCacheImage(path: string): Promise<string> {
    if (imageCache.has(path)) {
        return imageCache.get(path)!;
    }

    if (loadingPromises.has(path)) {
        return loadingPromises.get(path)!;
    }

    const loadingPromise = (async () => {
        try {
            const rs = await chatAPI.downloadFile(path);
            if (rs) {
                const url = window.URL.createObjectURL(rs);
                imageCache.set(path, url);
                return url;
            }
            throw new Error('Failed to download file');
        } finally {
            loadingPromises.delete(path);
        }
    })();

    loadingPromises.set(path, loadingPromise);
    return loadingPromise;
}

function ImageComp({ path, type, alt, className, classNameContainer = '', style, ...rest }: ImageCompProps) {
    const [url, setUrl] = useState<string>(() => imageCache.get(path) || '');
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(!imageCache.has(path));
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        let mounted = true;

        fetchAndCacheImage(path)
            .then((fetchedUrl) => {
                if (mounted) {
                    setUrl(fetchedUrl);
                    setLoading(false);
                }
            })
            .catch((e) => {
                if (mounted) {
                    setError(true);
                    setLoading(false);
                }
            });

        return () => {
            mounted = false;
        };
    }, [path]);

    const handleImageLoad = () => {
        setImageLoaded(true);
    };

    if (error) return null;

    if (loading || !url) {
        return (
            <div
                className={`relative overflow-hidden bg-gray-200 ${className || ''}`}
            >
                <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
            </div>
        );
    }

    return (
        <div className={`relative ${type === 'avatar' ? '' : 'w-full'} flex justify-center ${classNameContainer}`}>
            {!imageLoaded && (
                <div
                    className={`absolute ${type === 'avatar' ? 'rounded-full' : ''} inset-0 bg-gray-200 overflow-hidden`}
                    style={style}
                >
                    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
                </div>
            )}
            <img
                loading={"lazy"}
                src={url}
                className={`transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'} ${className || ''}`}
                {...rest}
                alt={alt}
                onLoad={handleImageLoad}
            />
        </div>
    );
}

export const clearImageCache = () => {
    imageCache.forEach(url => {
        window.URL.revokeObjectURL(url);
    });
    imageCache.clear();
};

export default React.memo(ImageComp);